import { toTitle } from "@src/helpers/text-helper";
import { ColumnsType } from "antd/es/table";

export interface AntdTableData<T = Record<string, any>> {
    columns: ColumnsType<T & { key: string }>;
    dataSource: (Record<string, any> & { key: string })[];
}

interface RetentionMappingParams {
    rawData: Record<string, any>[];
    variants: string[];

    hasBreakdown: boolean;
    breakdownField?: string;
    breakdownValues?: string[];
    fullMetricDayDiff: string[];
    metricType: "retention" | "sum_retention";
}

export function mapRetentionDataToAntdTable({
    rawData,
    variants,
    fullMetricDayDiff,
    hasBreakdown,
    breakdownField,
    breakdownValues,
    metricType,
}: RetentionMappingParams): AntdTableData {
    if (!rawData || rawData.length === 0) {
        return {
            columns: [],
            dataSource: [],
        };
    }

    if (!hasBreakdown) {
        const columns: ColumnsType<any> = [
            {
                title: metricType === "retention" ? "Retention Day" : "Sum Retention Day",
                dataIndex: "metricRetention",
                key: "metricRetention",
            },
            ...variants.map((variant) => ({
                title: toTitle(variant),
                dataIndex: variant,
                key: variant,
                render: (value: number) => `${(value * 100).toFixed(2)}%`,
            })),
        ];

        const dataSource = fullMetricDayDiff.map((field, index) => {
            const row: AntdTableData["dataSource"][number] = {
                key: field,
                metric: field,
                metricRetention: `Day ${index}`,
            };

            variants.forEach((variant) => {
                const variantData = rawData.find((item) => item["exp_group"] === variant);
                row[variant] = variantData ? variantData[field] : 0;
            });

            return row;
        });
        return { columns, dataSource };
    } else {
        if (!breakdownField || !breakdownValues) {
            return {
                columns: [],
                dataSource: [],
            };
        }

        const columns: ColumnsType<any> = [
            {
                title: breakdownField.charAt(0).toUpperCase() + breakdownField.slice(1),
                dataIndex: "breakdown",
                key: "breakdown",
            },
        ];

        const dataSource: any[] = [];

        breakdownValues.forEach((breakdownValue) => {
            const nestedColumns: ColumnsType<any> = [
                {
                    title: metricType === "retention" ? "Retention Day" : "Sum Retention Day",
                    dataIndex: "metricRetention",
                    key: "metricRetention",
                    width: 120,
                },
                ...variants.map((variant) => ({
                    title: toTitle(variant),
                    dataIndex: variant,
                    key: variant,
                    render: (value: number) => `${(value * 100).toFixed(2)}%`,
                })),
            ];

            const nestedDataSource = fullMetricDayDiff.map((field, dayIndex) => {
                const row: Record<string, any> = {
                    key: `${breakdownValue}-${field}`,
                    metric: field,
                    metricRetention: `Day ${dayIndex}`,
                };

                variants.forEach((variant) => {
                    const variantData = rawData.find(
                        (item) => item["exp_group"] === variant && item[breakdownField] === breakdownValue,
                    );
                    row[variant] = variantData ? variantData[field] : 0;
                });

                return row;
            });

            dataSource.push({
                key: `breakdown-${breakdownValue}`,
                breakdown: breakdownValue,
                nestedColumns,
                nestedDataSource,
            });
        });

        return { columns, dataSource };
    }
}
